import React from 'react';
import { t } from 'i18next';
import { ListColumnOptionType } from 'types/ListTypes';
import { convert24HourTo12HourFormat, formatDate } from 'services/utils/dateTimeUtils';
import { formatPriceCurrencyString } from 'services/utils/amountFormatting';

/*
 * renderCustomFieldColumn.tsx
 * Renders the stored answer value of a custom field column by the
 * custom field type, mirroring the view mode formatting of the
 * custom field renderer on the detail pages.
 */

// Custom field types whose stored value is a preset option key
const PRESET_FIELD_TYPES = ['yes_no', 'acceptable', 'compliant'];

// Resolves the label of an option value, falls back to the raw value
const getOptionLabel = (option: ListColumnOptionType, value: string): string => {
    return (option.options ?? []).find(choice => choice.value === value)?.label ?? String(value);
};

// Formats the answer value of a custom field column as display text
export const formatCustomFieldValue = (option: ListColumnOptionType, value: any, userLocale: string, timeFormat: '12h' | '24h'): string => {

    // Booleans show their translated yes or no value, also when false
    if (option.field_type === 'boolean') {
        if (value === true) return t('forms.true_value');
        if (value === false) return t('forms.false_value');
        return '';
    }

    // Missing values show an empty cell
    if (value === null || value === undefined || value === '') {
        return '';
    }

    // Dates are formatted on the user locale
    if (option.field_type === 'date') {
        return formatDate(String(value), userLocale);
    }

    // Times drop the seconds and follow the time format of the user
    if (option.field_type === 'time') {
        const time = String(value).slice(0, 5);
        return timeFormat === '12h' ? convert24HourTo12HourFormat(time) : time;
    }

    // Numbers are formatted on the user locale
    if (option.field_type === 'number') {
        return new Intl.NumberFormat(userLocale).format(Number(value));
    }

    // Amounts are formatted as a price in euros
    if (option.field_type === 'amount') {
        return formatPriceCurrencyString(String(value).replace(',', '.'), userLocale, 'EUR');
    }

    // Checkbox values list the labels of the checked options
    if (option.field_type === 'checkbox') {
        const values: string[] = Array.isArray(value) ? value : [];
        return values.map(checkedValue => getOptionLabel(option, checkedValue)).join(', ');
    }

    // Dropdown and tabs values show the label of the chosen option
    if (option.field_type === 'dropdown' || option.field_type === 'tabs') {
        return getOptionLabel(option, String(value));
    }

    // Preset options show their translated label
    if (PRESET_FIELD_TYPES.includes(option.field_type ?? '')) {
        return t(`custom_field.option_presets.${value}`, { defaultValue: String(value) });
    }

    // Text values show as they are
    return String(value);
};

// Renders the cell of a custom field column, a colored option shows as a label badge
export const renderCustomFieldColumn = (option: ListColumnOptionType, value: any, userLocale: string, timeFormat: '12h' | '24h'): JSX.Element => {

    // Show the chosen dropdown or tabs option as a badge when it has a color
    if ((option.field_type === 'dropdown' || option.field_type === 'tabs') && value !== null && value !== undefined && value !== '') {
        const choice = (option.options ?? []).find(optionChoice => optionChoice.value === String(value));
        if (choice?.color) {
            return (
                <div className='badge-container'>
                    <div className={`label-badge ${choice.color}`}>{choice.label}</div>
                </div>
            );
        }
    }
    return <div>{formatCustomFieldValue(option, value, userLocale, timeFormat)}</div>;
};
